'use client';

import { FC, useState } from 'react';
import clsx from 'clsx';
import FadeIn from '@/components/FadeIn/FadeIn';
import Image from 'next/image';
import { getImageUrl } from '@/lib/cdn';

interface FAQItem {
  question: string;
  answer: string;
}

interface ProductFAQProps {
  faqs: FAQItem[];
}

const ProductFAQ: FC<ProductFAQProps> = ({ faqs }) => {
  const [selected, setSelected] = useState<number | null>(null);

  // Split FAQs into two columns
  const midpoint = Math.ceil(faqs.length / 2);
  const leftFaqs = faqs.slice(0, midpoint);
  const rightFaqs = faqs.slice(midpoint);

  const handleToggle = (index: number) => {
    setSelected(selected === index ? null : index);
  };

  const FAQCard: FC<{ faq: FAQItem; index: number }> = ({ faq, index }) => (
    <div
      className="bg-white border border-slate-200 rounded-lg h-fit cursor-pointer"
      onClick={() => handleToggle(index)}
    >
      <div className="flex flex-col">
        <h3 className="flex justify-between items-center font-medium text-xl sm:text-2xl text-black py-4 lg:py-7.5 px-3 lg:px-9">
          {faq.question}
          <Image
            src={getImageUrl('icon/icon-plus-light.svg')}
            alt="plus"
            width={24}
            height={24}
            className={clsx(selected === index ? 'hidden' : 'block')}
          />
          <Image
            src={getImageUrl('icon/icon-minus-light.svg')}
            alt="minus"
            width={24}
            height={24}
            className={clsx(selected === index ? 'block' : 'hidden')}
          />
        </h3>
        <p
          className={clsx(
            'py-4 lg:py-7.5 px-3 lg:px-9 border-t border-slate-200 transition-all duration-300 text-sm sm:text-xl text-slate-600',
            selected === index ? 'block' : 'hidden'
          )}
        >
          {faq.answer}
        </p>
      </div>
    </div>
  );

  return (
    <section className="py-16 md:py-24">
      <div className="container-fluid px-4 md:px-7.5 mx-auto" style={{ maxWidth: 'clamp(1366px, 85vw, 1800px)' }}>
        <FadeIn variant="up" delay={0.1}>
          {/* Title - no mb logo */}
          <div className="text-center mb-12">
            <h2
              className="font-medium text-black"
              style={{ fontSize: 'clamp(1.75rem, 3vw, 3rem)' }}
            >
              Frequently <span style={{ color: '#094FFE' }}>asked</span> questions
            </h2>
          </div>

          {/* FAQ Grid - matching homepage layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-9 gap-y-6">
            {/* Left Column */}
            {leftFaqs.map((faq, idx) => (
              <FAQCard key={idx} faq={faq} index={idx} />
            ))}
            {/* Right Column */}
            {rightFaqs.map((faq, idx) => (
              <FAQCard key={idx + midpoint} faq={faq} index={idx + midpoint} />
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default ProductFAQ;
